import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import classNames from "classnames";
import * as React from "react";

import { Check, TriangleAlert } from "@/shared/foundation/icons";

import { Tooltip } from "../Tooltip";

import styles from "./Stepper.module.css";

type BaseUIClassName<State> =
  string | ((state: State) => string | undefined) | undefined;

function withHouseClass<State>(
  houseClass: string,
  className: BaseUIClassName<State>,
) {
  return (state: State) =>
    classNames(
      houseClass,
      typeof className === "function" ? className(state) : className,
    );
}

export type StepperProps = BaseTabs.Root.Props;

/**
 * An ordered sequence of parts: a row with an item for each part, and under
 * it the content of the open part. Holds the open part's value; pair it with
 * a {@link StepperList} of {@link StepperItem}s and a {@link StepperPanel} per
 * item.
 *
 * Each item shows its place in the order, whether it is done or needs
 * attention, and one line about what it holds. The caller decides which items
 * can be opened: an item with a `disabledReason` stays focusable and says
 * why, and selecting it does nothing.
 *
 * @example
 * ```tsx
 * <Stepper value={part} onValueChange={setPart}>
 *   <StepperList aria-label="Setup" end={<RobotoButton>Next</RobotoButton>}>
 *     <StepperItem value="data" label="Data" detail="3 sources" status="complete" />
 *     <StepperItem value="display" label="Display" detail="Choose a kind" />
 *   </StepperList>
 *   <StepperPanel value="data" keepMounted>…</StepperPanel>
 *   <StepperPanel value="display" keepMounted>…</StepperPanel>
 * </Stepper>
 * ```
 */
export function Stepper({ className, ...props }: StepperProps) {
  return (
    <BaseTabs.Root
      className={withHouseClass(styles.root, className)}
      {...props}
    />
  );
}

export interface StepperListProps {
  "aria-label": string;
  /** The {@link StepperItem}s, in order. */
  children: React.ReactNode;
  /** Drawn at the end of the row, after the last item: a Next button, for
   *  example. */
  end?: React.ReactNode;
  /** Applied to the row: the items, the end slot and the rule. */
  className?: string;
}

/**
 * The row of items over a rule. The Left and Right arrow keys move focus
 * between the items, stopping at the ends; Enter or Space opens the focused
 * one.
 */
export function StepperList({
  "aria-label": ariaLabel,
  children,
  end,
  className,
}: StepperListProps) {
  return (
    <div className={classNames(styles.strip, className)}>
      <BaseTabs.List
        aria-label={ariaLabel}
        className={styles.list}
        loopFocus={false}
      >
        {children}
      </BaseTabs.List>
      {end === undefined ? null : <div className={styles.end}>{end}</div>}
    </div>
  );
}

/** Whether an item is done or needs attention. An item with neither shows
 *  its position in the order. */
export type StepperItemStatus = "complete" | "attention";

export interface StepperItemProps {
  value: string;
  label: React.ReactNode;
  /** One short line under the label: what the item holds, or what is left
   *  to do in it. */
  detail?: React.ReactNode;
  /** `complete` draws a check mark and `attention` a warning mark in the
   *  danger color, in place of the item's position. */
  status?: StepperItemStatus;
  /** Set while the item cannot be opened. The item stays focusable, is
   *  `aria-disabled`, and shows the reason in a tooltip and in its accessible
   *  name. */
  disabledReason?: string;
}

const STATUS_TEXT: Record<StepperItemStatus, string> = {
  complete: "Done.",
  attention: "Needs attention.",
};

/** One item of a {@link StepperList}: a mark, the label, and the detail line
 *  under it, joined to the next item by a line. The current item is
 *  underlined. The accessible name is the label, the detail, the status in
 *  words, and the disabled reason, in that order. */
export const StepperItem = React.forwardRef<HTMLElement, StepperItemProps>(
  function StepperItem({ value, label, detail, status, disabledReason }, ref) {
    const disabled = disabledReason !== undefined;
    // The item always sits in the tooltip, which is switched off while the
    // item can be opened, so an item that gains or loses its reason keeps its
    // element and its focus.
    return (
      <Tooltip>
        <Tooltip.Trigger
          delay={0}
          disabled={!disabled}
          render={
            <BaseTabs.Tab
              ref={ref}
              value={value}
              disabled={disabled}
              className={styles.item}
              data-status={status}
            >
              <span className={styles.mark} aria-hidden>
                <StatusMark status={status} />
              </span>
              <span className={styles.text}>
                <span className={styles.label}>{label}</span>{" "}
                {detail === undefined ? null : (
                  <span className={styles.detail}>{detail}</span>
                )}{" "}
                {/* The mark is a picture, so its status is said in words. */}
                {status === undefined ? null : (
                  <span className="sr-only">{STATUS_TEXT[status]}</span>
                )}{" "}
                {disabledReason === undefined ? null : (
                  <span className="sr-only">{disabledReason}</span>
                )}
              </span>
            </BaseTabs.Tab>
          }
        />
        <Tooltip.Content side="bottom">{disabledReason}</Tooltip.Content>
      </Tooltip>
    );
  },
);

function StatusMark({ status }: { status: StepperItemStatus | undefined }) {
  switch (status) {
    case "complete": {
      return <Check />;
    }
    case "attention": {
      return <TriangleAlert />;
    }
    case undefined: {
      // The position is drawn by the stylesheet, which counts the items.
      return null;
    }
  }
}

export type StepperPanelProps = BaseTabs.Panel.Props;

/** The content shown while the {@link StepperItem} with the same `value` is
 *  open. Pass `keepMounted` to keep what was entered in a part while another
 *  is open. */
export function StepperPanel({ className, ...props }: StepperPanelProps) {
  return (
    <BaseTabs.Panel
      className={withHouseClass(styles.panel, className)}
      {...props}
    />
  );
}
